// Что панель знает о происходящем на экране.
//
// Панели рисует каркас, и от экрана им достаётся только идентификатор из адреса. Остальное они
// берут ОТТУДА ЖЕ, откуда экран: период — из `?f.official`, строки — из той же сетки. Своего
// запроса за строкой в контракте нет, и заводить в панели вторую копию состояния значит
// однажды показать в ней остаток, которого в сетке уже нет.
//
// Сетка для панели берётся БЕЗ отбора: строка обязана найтись, даже если отбор по состоянию её
// со сетки убрал — ссылка на строку приходит и от коллеги, у которого отбор был другой.

import { useLocation } from 'react-router';

import type { OfficialPeriodOut, OfficialRowOut } from '@/shared/api/endpoints/official';
import { parseSearch } from '@/shared/routing/search';

import { FILTER_OFFICIAL_PERIOD } from '../../route';
import { useGrid, usePeriods } from '../queries';

/** Период, открытый на экране. Пусто в адресе — самый свежий, как и на самом экране. */
export function useCurrentPeriodId(): string | null {
  const location = useLocation();
  const периоды = usePeriods();
  const явный = parseSearch(location.search).filters[FILTER_OFFICIAL_PERIOD];
  if (явный !== undefined && явный !== '') {
    return явный;
  }
  return периоды.data?.items[0]?.id ?? null;
}

export interface СтрокаПанели {
  readonly row: OfficialRowOut | null;
  readonly period: OfficialPeriodOut | null;
  readonly weeks: readonly number[];
  readonly states: readonly { code: string; title: string }[];
  readonly isPending: boolean;
  readonly error: unknown;
  /**
   * Повторить запрос.
   *
   * Ошибка ЗАПРОСА без повтора оставляет человеку единственный выход — перезагрузить страницу,
   * а вместе с ней уходит вся открытая стопка панелей и незаконченная работа.
   */
  readonly retry: () => void;
}

/** Строка периода по её идентификатору — из той же сетки, что видит экран. */
export function useRowFromGrid(rowId: string): СтрокаПанели {
  const periodId = useCurrentPeriodId();
  const сетка = useGrid(periodId, {});
  return {
    row: сетка.data?.rows.find((строка) => строка.id === rowId) ?? null,
    period: сетка.data?.period ?? null,
    weeks: сетка.data?.weeks ?? [],
    states: сетка.data?.states ?? [],
    isPending: сетка.isPending,
    error: сетка.error,
    retry: () => void сетка.refetch(),
  };
}

/**
 * Момент выплаты из календарной даты.
 *
 * Девять утра по Гринвичу, а не полночь: сервер печатает дату выплаты по всемирному времени, и
 * полночь при часовых поясах России (от +2 до +12) съезжала бы на день назад или вперёд. В
 * девять утра любой российский пояс даёт тот же календарный день.
 */
export function вМоментВыплаты(iso: string): string {
  return `${iso}T09:00:00Z`;
}
